import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, Handshake, Loader2, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiMaybe } from '../services/apiClient';
import { listGenerateTypes, listGenerateSessions } from '../features/generate/generateApi';
import { fmtShortDateTime as formatTime } from '../utils/format';

// Self-serve home for non-legal users: surface the things they can do (start an
// intake, find an agreement) plus their own in-flight work. New sections
// (announcements, etc.) can be dropped in later as plain cards — no framework.
function typeLabel(typeKey) {
  if (typeKey === 'grid') return 'Grid Suite';
  if (typeKey === 'nda') return 'NDA';
  if (typeKey === 'tprm') return 'Vendor onboarding';
  if (!typeKey) return 'Intake';
  return typeKey.charAt(0).toUpperCase() + typeKey.slice(1);
}

function statusChip(status) {
  if (['completed', 'approved'].includes(status)) return 'chip green';
  if (['in_progress', 'submitted', 'under_review'].includes(status)) return 'chip blue';
  return 'chip gray';
}

const rowStyle = (first) => ({
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
  padding: '10px 16px', borderTop: first ? 0 : '0.5px solid var(--border-primary)',
  textDecoration: 'none', color: 'inherit',
});

function ActionCard({ icon: Icon, title, description, cta, onClick }) {
  return (
    <button
      className="card"
      onClick={onClick}
      style={{
        textAlign: 'left', cursor: 'pointer', padding: 18, border: '1px solid var(--border-primary)',
        display: 'flex', flexDirection: 'column', height: '100%',
      }}
    >
      <div className="row" style={{ gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
        <Icon className="w-4 h-4" style={{ color: 'var(--icon-secondary)', flexShrink: 0, marginTop: 1 }} />
        <span style={{ fontWeight: 550, fontSize: 14 }}>{title}</span>
      </div>
      {description && <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.45 }}>{description}</div>}
      <div className="row" style={{ gap: 4, marginTop: 'auto', paddingTop: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
        {cta} <ArrowRight className="w-3 h-3" />
      </div>
    </button>
  );
}

export default function UserDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // Each fetch resolves null on failure (apiMaybe logs it). Track which ones
  // failed so a broken section says so instead of masquerading as empty.
  const [failed, setFailed] = useState({ types: false, intakes: false, requests: false });

  useEffect(() => {
    let alive = true;
    Promise.all([
      listGenerateTypes(),
      listGenerateSessions(),
      apiMaybe('/requests'),
    ]).then(([t, s, r]) => {
      if (!alive) return;
      setTypes((t?.types || []).filter((x) => x.available !== false));
      setIntakes((s?.sessions || []).slice(0, 6));
      setRequests((r?.requests || []).slice(0, 6));
      setFailed({ types: !t, intakes: !s, requests: !r });
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const firstName = (user?.name || '').split(' ')[0] || user?.email?.split('@')[0] || 'there';

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Welcome</div>
          <h1 className="page-title">Hi {firstName}</h1>
          <div className="page-subtitle">Start something new, or pick up where you left off.</div>
        </div>
      </div>

      {failed.types && (
        <div style={{ fontSize: 12.5, marginBottom: 12, color: 'var(--text-yellow)' }}>
          Couldn't load the intake options — refresh to try again.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14, marginBottom: 30 }}>
        {types.map((t) => (
          <ActionCard
            key={t.key}
            icon={t.key === 'tprm' ? Handshake : FileText}
            title={t.displayName}
            description={t.description}
            cta="Start"
            onClick={() => navigate(`/intake/${t.key}`)}
          />
        ))}
        <ActionCard
          icon={Search}
          title="Find an agreement"
          description="Search and view existing contracts."
          cta="Browse"
          onClick={() => navigate('/contracts')}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
        <div className="card">
          <div className="card-header compact" style={{ justifyContent: 'space-between' }}>
            <span className="card-title">Your recent intakes</span>
            <Link to="/intake" className="btn ghost xs">View all</Link>
          </div>
          <div className="card-body flush">
            {loading ? (
              <div style={{ padding: 28, textAlign: 'center' }}><Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--icon-secondary)' }} /></div>
            ) : failed.intakes ? (
              <div className="muted" style={{ padding: 24, fontSize: 12.5, textAlign: 'center' }}>Couldn't load your intakes — refresh to try again.</div>
            ) : intakes.length === 0 ? (
              <div className="muted" style={{ padding: 24, fontSize: 12.5, textAlign: 'center' }}>Nothing yet — start one above.</div>
            ) : intakes.map((s, i) => (
              <Link key={s.id} to={`/intake/sessions/${s.id}`} style={rowStyle(i === 0)}>
                <div style={{ minWidth: 0 }}>
                  <div className="ellipsis" style={{ fontSize: 13 }}>{s.prospectName || 'Untitled intake'}</div>
                  <div className="muted" style={{ fontSize: 11 }}>{typeLabel(s.typeKey)} · {formatTime(s.updatedAt)}</div>
                </div>
                <span className={statusChip(s.status)}>{s.status === 'in_progress' ? 'in progress' : s.status}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header compact" style={{ justifyContent: 'space-between' }}>
            <span className="card-title">Your vendor requests</span>
            <Link to="/tprm" className="btn ghost xs">View all</Link>
          </div>
          <div className="card-body flush">
            {loading ? (
              <div style={{ padding: 28, textAlign: 'center' }}><Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--icon-secondary)' }} /></div>
            ) : failed.requests ? (
              <div className="muted" style={{ padding: 24, fontSize: 12.5, textAlign: 'center' }}>Couldn't load your requests — refresh to try again.</div>
            ) : requests.length === 0 ? (
              <div className="muted" style={{ padding: 24, fontSize: 12.5, textAlign: 'center' }}>No vendor onboarding requests yet.</div>
            ) : requests.map((r, i) => (
              <Link key={r.id} to={`/tprm/${r.id}`} style={rowStyle(i === 0)}>
                <div style={{ minWidth: 0 }}>
                  <div className="ellipsis" style={{ fontSize: 13 }}>{r.vendorName || 'Unnamed vendor'}</div>
                  <div className="muted" style={{ fontSize: 11 }}>{formatTime(r.updatedAt || r.createdAt)}</div>
                </div>
                <span className={statusChip(r.status)}>{r.status}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
